import React, { useState, useEffect, useRef } from 'react';
import { DB, uploadImage } from '../../data/database';

/* ── Default content for each page ── */
const DEFAULTS = {
  website_homepage: {
    hero: { enabled: true, background_mode: 'animation', background_image_url: '', background_video_url: '', overlay_opacity: 0.7, eyebrow: 'EST. 2008 · SHARJAH, UAE', headline: 'Pre-owned, certified, ready to drive.', headline_accent_word: 'certified', subheadline: 'Hand-picked used vehicles and genuine spare parts, exported worldwide. Inquire by WhatsApp or call for a tailored quote, photos and shipping numbers the same day.', primary_cta_text: 'Explore stock →', secondary_cta_text: 'Spare parts catalog', show_live_yard_badge: true, show_container_count: true },
    featured: { enabled: true, eyebrow: 'CURATED BY OUR TEAM · LIVE', heading: 'Special units', view_all_text: 'View all stock →', mode: 'auto', auto_limit: 6 },
    recent: { enabled: true, eyebrow: 'INVENTORY · UPDATED 2 MIN AGO', heading: 'New arrivals', auto_limit: 8 },
    parts: { enabled: true, eyebrow: 'SPARE PARTS', heading: 'Genuine, used parts, in stock.', heading_accent_word: 'in stock.', subheading: 'Search by make, model & year — every part shipped from our warehouse, visit our yard today!', cta_text: 'Browse parts catalog →' },
    value_pillars: { enabled: true, pillars: [
      { title: 'Hand-picked stock', description: 'Every unit walked through and selected by our buyers.' },
      { title: 'Export ready', description: 'GCC, Africa, CIS and Far-East export documentation handled in-house.' },
      { title: 'Trade-friendly', description: 'Dealers, garages and exporters can register a trade account.' },
    ]},
    shipping_process: { enabled: true, heading: 'From the yard to your port.', steps: [
      { number: '01', title: 'Inquiry', description: 'WhatsApp or call — share the make, model, year and destination port.' },
      { number: '02', title: 'Live quote', description: 'We come back with vehicle photos, condition notes and an all-in CIF/FOB number.' },
      { number: '03', title: 'Loading', description: 'Containers loaded at our Sharjah yard, sealed and trucked to Jebel Ali.' },
      { number: '04', title: 'On the water', description: 'Tracking shared end-to-end — documents handled in-house.' },
    ]},
  },
  website_about: {
    hero: { enabled: true, eyebrow: 'ABOUT BIGSTAR', headline: 'From a Sharjah yard to ports across the world.', headline_accent_words: 'to ports', description: 'BigStar is a used-vehicle and genuine spare-parts yard headquartered in Sharjah, UAE.', description2: "We don't list prices online — every shipment is quoted live.", primary_cta_text: 'Talk to us on WhatsApp', secondary_cta_text: 'Visit the yard →' },
    destinations: [
      { name: 'RUSSIA', lat: 55.7, lng: 37.6 }, { name: 'BELGIUM', lat: 50.8, lng: 4.3 },
      { name: 'KAZAKHSTAN', lat: 51.1, lng: 71.4 }, { name: 'AZERBAIJAN', lat: 40.4, lng: 49.8 },
      { name: 'UZBEKISTAN', lat: 41.3, lng: 69.2 }, { name: 'PAKISTAN', lat: 30.3, lng: 69.3 },
      { name: 'HONG KONG', lat: 22.3, lng: 114.1 }, { name: 'KENYA', lat: -1.3, lng: 36.8 },
    ],
    yard: { enabled: true, eyebrow: 'MAIN YARD', heading: 'Industrial Area No. 3, Sharjah.', description: 'Our yard sits 25 minutes from Jebel Ali Port and 15 from Sharjah Airport.', address: 'Industrial Area No. 3, Sharjah, UAE', hours: 'Sat-Thu · 8:00 AM – 9:00 PM', phone1: '06-5433296', phone2: '050-8583625' },
  },
  website_contact: {
    hero: { eyebrow: 'CONTACT', heading: 'Get in touch.' },
    whatsapp: { heading: 'WhatsApp us', description: 'Fastest way to reach a sales rep — typical response under 5 min.' },
    form: { enabled: true, subjects: ['General inquiry', 'Vehicle inquiry', 'Parts inquiry', 'Trade account', 'Shipping quote', 'Other'] },
  },
  website_parts: {
    hero: { enabled: true, eyebrow: 'SPARE PARTS CATALOG', headline: 'Genuine parts. Fit-checked. In stock.', headline_accent_word: 'In stock.', subheading: 'Find the right part for your vehicle.', cta_text: 'Find parts →' },
    categories: [
      { name: 'Cut Types', enabled: true }, { name: 'Engine & Trans', enabled: true },
      { name: 'Drivetrain', enabled: true }, { name: 'Suspension', enabled: true },
      { name: 'Body Panels', enabled: true }, { name: 'Lights', enabled: true },
      { name: 'Cooling & AC', enabled: true }, { name: 'Interior', enabled: true },
    ],
  },
  website_inventory: {
    hero: { eyebrow: 'INVENTORY', heading: 'Used cars', subheading: 'Every vehicle hand-picked, GCC or US spec, ready for inspection at the yard. Prices on inquiry — WhatsApp or call for a tailored quote.' },
    default_view: 'grid',
  },
  website_trade: {
    hero: { eyebrow: 'TRADE ACCOUNT', heading: 'Apply for a buyer account.', subheading: 'Get access to pricing, bulk ordering, and dedicated support. Businesses and individual buyers welcome.' },
    required_documents: [
      { name: 'Trade license', required: true, hint: 'PDF or image · max 5MB · must be valid' },
      { name: 'Authorised signatory Emirates ID', required: true, hint: 'Both sides · clear scan' },
      { name: 'VAT registration certificate', required: false, hint: 'Optional — required for net pricing' },
      { name: 'Memorandum of Association', required: false, hint: 'Optional — for corporate signatories' },
    ],
  },
  website_footer: {
    description: "UAE's premium destination for pre-owned vehicles and genuine spare parts.",
    columns: [
      { title: 'INVENTORY', enabled: true, links: [{ label: 'All cars', enabled: true }, { label: 'Featured', enabled: true }, { label: 'Newly arrived', enabled: true }] },
      { title: 'SPARE PARTS', enabled: true, links: [{ label: 'Engine', enabled: true }, { label: 'Brakes', enabled: true }, { label: 'Suspension', enabled: true }] },
      { title: 'COMPANY', enabled: true, links: [{ label: 'About', enabled: true }, { label: 'Locations', enabled: true }, { label: 'Become a Buyer', enabled: true }] },
      { title: 'SUPPORT', enabled: true, links: [{ label: 'Contact', enabled: true }, { label: 'WhatsApp', enabled: true }] },
    ],
    bottom_links: [{ label: 'Privacy', enabled: true }, { label: 'Terms', enabled: true }, { label: 'Cookie policy', enabled: false }],
    copyright_text: 'BIGSTAR USED CARS & SPARE PARTS LLC · SHARJAH',
    show_whatsapp_button: true, show_phone_button: true,
  },
};

const PAGES = [
  { id: 'website_homepage', label: 'Homepage', icon: '🌐' },
  { id: 'website_about', label: 'About', icon: '📄' },
  { id: 'website_contact', label: 'Contact', icon: '📄' },
  { id: 'website_parts', label: 'Spare parts', icon: '🔧' },
  { id: 'website_inventory', label: 'Used Cars', icon: '🚗' },
  { id: 'website_trade', label: 'Trade page', icon: '📋' },
  { id: 'website_footer', label: 'Footer', icon: '📄' },
];

/* ══════════════════════════════════════════════════════ */
/*  SHARED UI — defined OUTSIDE parent to prevent        */
/*  remounting on every keystroke                         */
/* ══════════════════════════════════════════════════════ */
const SectionCard = ({ num, title, enabled, onToggle, children }) => (
  <div style={{ background: 'var(--bg2)', borderRadius: 12, border: '1px solid var(--border)', marginBottom: 16, overflow: 'hidden' }}>
    <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'monospace' }}>Section {num}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text1)' }}>{title}</span>
      </div>
      {onToggle && <ToggleSwitch on={enabled} onChange={onToggle} />}
    </div>
    {enabled !== false && <div style={{ padding: '16px 20px' }}>{children}</div>}
  </div>
);

const ToggleSwitch = ({ on, onChange }) => (
  <div onClick={onChange} style={{ width: 42, height: 24, borderRadius: 12, background: on ? '#22C55E' : 'var(--bg3)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', border: '1px solid var(--border)' }}>
    <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: on ? 21 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
  </div>
);

const Fld = ({ label, hint, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>{label}</label>
    {children}
    {hint && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>{hint}</div>}
  </div>
);

/* ══════════════════════════════════════════════════════ */
/*  PAGE EDITORS — all defined OUTSIDE CMSSiteContent    */
/*  to prevent input focus loss on re-render              */
/* ══════════════════════════════════════════════════════ */

function HomepageEditor({ data, update }) {
  const h = data.hero || {};
  const f = data.featured || {};
  const r = data.recent || {};
  const pt = data.parts || {};
  const vp = data.value_pillars || {};
  const sp = data.shipping_process || {};
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleBgUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxSize = file.type.startsWith('video') ? 20 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(file.type.startsWith('video') ? 'Video must be under 20MB.' : 'Image must be under 5MB.');
      e.target.value = ''; return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const url = await uploadImage(ev.target.result, 'branding');
        if (h.background_mode === 'video') {
          update('hero.background_video_url', url || ev.target.result);
        } else {
          update('hero.background_image_url', url || ev.target.result);
        }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Upload failed: ' + (err.message || 'Please try again.'));
      setUploading(false);
    }
    e.target.value = '';
  };

  return (
    <>
      <SectionCard num="01" title="Hero" enabled={h.enabled} onToggle={() => update('hero.enabled', !h.enabled)}>
        <Fld label="Background mode">
          <div style={{ display: 'flex', gap: 6 }}>
            {['animation', 'image', 'video'].map(m => (
              <button key={m} onClick={() => update('hero.background_mode', m)} className={`btn btn-sm ${h.background_mode === m ? 'btn-primary' : 'btn-secondary'}`} style={{ textTransform: 'capitalize', fontSize: 11 }}>{m === 'animation' ? 'Animated grid' : m.charAt(0).toUpperCase() + m.slice(1)}</button>
            ))}
          </div>
        </Fld>

        {/* BUG 2 FIX: Upload UI for image/video */}
        {h.background_mode === 'image' && (
          <Fld label="Background image">
            <div style={{ padding: 16, border: '2px dashed var(--border)', borderRadius: 10, marginBottom: 8 }}>
              {h.background_image_url ? (
                <div>
                  <img src={h.background_image_url} alt="" style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 6, marginBottom: 8 }} />
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()}>{uploading ? 'Uploading...' : 'Replace'}</button>
                    <button className="btn btn-secondary btn-sm" style={{ color: 'var(--red)' }} onClick={() => update('hero.background_image_url', '')}>Remove</button>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>No image uploaded</div>
                  <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()} disabled={uploading}>{uploading ? 'Uploading...' : '📷 Upload Image'}</button>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 6 }}>JPG, PNG, WebP · Recommended: 1920×1080+</div>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleBgUpload} />
            </div>
            <Fld label={`Overlay darkness: ${Math.round((h.overlay_opacity ?? 0.7) * 100)}%`}>
              <input type="range" min="0" max="100" value={Math.round((h.overlay_opacity ?? 0.7) * 100)} onChange={e => update('hero.overlay_opacity', e.target.value / 100)} style={{ width: '100%', accentColor: 'var(--blue)' }} />
            </Fld>
          </Fld>
        )}
        {h.background_mode === 'video' && (
          <Fld label="Background video">
            <div style={{ padding: 16, border: '2px dashed var(--border)', borderRadius: 10, marginBottom: 8 }}>
              {h.background_video_url ? (
                <div>
                  <video src={h.background_video_url} muted style={{ width: '100%', maxHeight: 120, borderRadius: 6, marginBottom: 8 }} />
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()}>{uploading ? 'Uploading...' : 'Replace'}</button>
                    <button className="btn btn-secondary btn-sm" style={{ color: 'var(--red)' }} onClick={() => update('hero.background_video_url', '')}>Remove</button>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>No video uploaded</div>
                  <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()} disabled={uploading}>{uploading ? 'Uploading...' : '🎥 Upload Video'}</button>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 6 }}>MP4 (max 20MB) · Autoplay muted loop</div>
                </div>
              )}
              <input ref={fileRef} type="file" accept="video/mp4,video/*" style={{ display: 'none' }} onChange={handleBgUpload} />
            </div>
            <Fld label={`Overlay darkness: ${Math.round((h.overlay_opacity ?? 0.7) * 100)}%`}>
              <input type="range" min="0" max="100" value={Math.round((h.overlay_opacity ?? 0.7) * 100)} onChange={e => update('hero.overlay_opacity', e.target.value / 100)} style={{ width: '100%', accentColor: 'var(--blue)' }} />
            </Fld>
          </Fld>
        )}

        <Fld label="Eyebrow"><input className="form-input" style={{ fontSize: 13 }} value={h.eyebrow || ''} onChange={e => update('hero.eyebrow', e.target.value)} /></Fld>
        <Fld label="Headline"><input className="form-input" style={{ fontSize: 13 }} value={h.headline || ''} onChange={e => update('hero.headline', e.target.value)} /></Fld>
        <Fld label="Accent word" hint="Shown in italic blue (Instrument Serif)"><input className="form-input" style={{ fontSize: 13 }} value={h.headline_accent_word || ''} onChange={e => update('hero.headline_accent_word', e.target.value)} /></Fld>
        <Fld label="Subheadline"><textarea className="form-input" style={{ fontSize: 13, resize: 'vertical' }} rows={3} value={h.subheadline || ''} onChange={e => update('hero.subheadline', e.target.value)} /></Fld>
        <div className="form-row">
          <Fld label="Primary CTA"><input className="form-input" style={{ fontSize: 13 }} value={h.primary_cta_text || ''} onChange={e => update('hero.primary_cta_text', e.target.value)} /></Fld>
          <Fld label="Secondary CTA"><input className="form-input" style={{ fontSize: 13 }} value={h.secondary_cta_text || ''} onChange={e => update('hero.secondary_cta_text', e.target.value)} /></Fld>
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={h.show_live_yard_badge !== false} onChange={e => update('hero.show_live_yard_badge', e.target.checked)} style={{ accentColor: 'var(--green)' }} /> Show live yard badge
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={h.show_container_count !== false} onChange={e => update('hero.show_container_count', e.target.checked)} style={{ accentColor: 'var(--green)' }} /> Show container count
          </label>
        </div>
      </SectionCard>

      <SectionCard num="02" title="Curated stock" enabled={f.enabled} onToggle={() => update('featured.enabled', !f.enabled)}>
        <Fld label="Section heading"><input className="form-input" style={{ fontSize: 13 }} value={f.heading || ''} onChange={e => update('featured.heading', e.target.value)} /></Fld>
        <Fld label="Eyebrow"><input className="form-input" style={{ fontSize: 13 }} value={f.eyebrow || ''} onChange={e => update('featured.eyebrow', e.target.value)} /></Fld>
        <Fld label="View all text"><input className="form-input" style={{ fontSize: 13 }} value={f.view_all_text || ''} onChange={e => update('featured.view_all_text', e.target.value)} /></Fld>
        <Fld label="Mode">
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => update('featured.mode', 'auto')} className={`btn btn-sm ${f.mode === 'auto' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: 11 }}>Auto · {f.auto_limit || 6} newest live</button>
            <button onClick={() => update('featured.mode', 'manual')} className={`btn btn-sm ${f.mode === 'manual' ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: 11 }}>Manual selection</button>
          </div>
        </Fld>
      </SectionCard>

      <SectionCard num="03" title="New arrivals" enabled={r.enabled !== false} onToggle={() => update('recent.enabled', !(r.enabled !== false))}>
        <Fld label="Heading"><input className="form-input" style={{ fontSize: 13 }} value={r.heading || ''} onChange={e => update('recent.heading', e.target.value)} /></Fld>
        <Fld label="Eyebrow"><input className="form-input" style={{ fontSize: 13 }} value={r.eyebrow || ''} onChange={e => update('recent.eyebrow', e.target.value)} /></Fld>
        <Fld label="Auto limit"><input className="form-input" style={{ fontSize: 13 }} type="number" value={r.auto_limit || 8} onChange={e => update('recent.auto_limit', parseInt(e.target.value) || 8)} /></Fld>
      </SectionCard>

      <SectionCard num="04" title="Trade lane / How we ship" enabled={sp.enabled} onToggle={() => update('shipping_process.enabled', !sp.enabled)}>
        <Fld label="Heading"><input className="form-input" style={{ fontSize: 13 }} value={sp.heading || ''} onChange={e => update('shipping_process.heading', e.target.value)} /></Fld>
        {(sp.steps || []).map((step, i) => (
          <div key={`step-${i}`} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--blue)', marginTop: 8, width: 24, flexShrink: 0 }}>{step.number}</span>
            <input className="form-input" style={{ width: 120, flexShrink: 0, fontSize: 13 }} value={step.title || ''} onChange={e => { const s = [...sp.steps]; s[i] = { ...s[i], title: e.target.value }; update('shipping_process.steps', s); }} />
            <input className="form-input" style={{ fontSize: 13 }} value={step.description || ''} onChange={e => { const s = [...sp.steps]; s[i] = { ...s[i], description: e.target.value }; update('shipping_process.steps', s); }} />
            <button onClick={() => update('shipping_process.steps', sp.steps.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14, marginTop: 6 }}>✕</button>
          </div>
        ))}
        <button onClick={() => update('shipping_process.steps', [...(sp.steps || []), { number: String((sp.steps?.length || 0) + 1).padStart(2, '0'), title: '', description: '' }])} className="btn btn-secondary btn-sm" style={{ fontSize: 10 }}>+ Add step</button>
      </SectionCard>

      <SectionCard num="05" title="Value pillars" enabled={vp.enabled} onToggle={() => update('value_pillars.enabled', !vp.enabled)}>
        {(vp.pillars || []).map((p, i) => (
          <div key={`pillar-${i}`} style={{ marginBottom: 12, padding: 12, background: 'var(--bg3)', borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)' }}>Pillar {i + 1}</span>
              <button onClick={() => update('value_pillars.pillars', vp.pillars.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 12 }}>✕</button>
            </div>
            <input className="form-input" style={{ fontSize: 13, marginBottom: 6 }} value={p.title || ''} placeholder="Title" onChange={e => { const ps = [...vp.pillars]; ps[i] = { ...ps[i], title: e.target.value }; update('value_pillars.pillars', ps); }} />
            <textarea className="form-input" style={{ fontSize: 13, resize: 'vertical' }} rows={2} value={p.description || ''} placeholder="Description" onChange={e => { const ps = [...vp.pillars]; ps[i] = { ...ps[i], description: e.target.value }; update('value_pillars.pillars', ps); }} />
          </div>
        ))}
        <button onClick={() => update('value_pillars.pillars', [...(vp.pillars || []), { title: '', description: '' }])} className="btn btn-secondary btn-sm" style={{ fontSize: 10 }}>+ Add pillar</button>
      </SectionCard>

      <SectionCard num="06" title="Parts catalog" enabled={pt.enabled} onToggle={() => update('parts.enabled', !pt.enabled)}>
        <Fld label="Heading"><input className="form-input" style={{ fontSize: 13 }} value={pt.heading || ''} onChange={e => update('parts.heading', e.target.value)} /></Fld>
        <Fld label="Accent word"><input className="form-input" style={{ fontSize: 13 }} value={pt.heading_accent_word || ''} onChange={e => update('parts.heading_accent_word', e.target.value)} /></Fld>
        <Fld label="Subheading"><textarea className="form-input" style={{ fontSize: 13, resize: 'vertical' }} rows={2} value={pt.subheading || ''} onChange={e => update('parts.subheading', e.target.value)} /></Fld>
        <Fld label="CTA button text"><input className="form-input" style={{ fontSize: 13 }} value={pt.cta_text || ''} onChange={e => update('parts.cta_text', e.target.value)} /></Fld>
      </SectionCard>
    </>
  );
}

function AboutEditor({ data, update }) {
  const h = data.hero || {};
  const y = data.yard || {};
  const dests = data.destinations || [];

  return (
    <>
      <SectionCard num="01" title="Hero" enabled={h.enabled} onToggle={() => update('hero.enabled', !h.enabled)}>
        <Fld label="Eyebrow"><input className="form-input" style={{ fontSize: 13 }} value={h.eyebrow || ''} onChange={e => update('hero.eyebrow', e.target.value)} /></Fld>
        <Fld label="Headline"><input className="form-input" style={{ fontSize: 13 }} value={h.headline || ''} onChange={e => update('hero.headline', e.target.value)} /></Fld>
        <Fld label="Accent words" hint="Shown in blue italic"><input className="form-input" style={{ fontSize: 13 }} value={h.headline_accent_words || ''} onChange={e => update('hero.headline_accent_words', e.target.value)} /></Fld>
        <Fld label="Description"><textarea className="form-input" style={{ fontSize: 13, resize: 'vertical' }} rows={4} value={h.description || ''} onChange={e => update('hero.description', e.target.value)} /></Fld>
        <Fld label="Description (paragraph 2)"><textarea className="form-input" style={{ fontSize: 13, resize: 'vertical' }} rows={3} value={h.description2 || ''} onChange={e => update('hero.description2', e.target.value)} /></Fld>
        <div className="form-row">
          <Fld label="Primary CTA"><input className="form-input" style={{ fontSize: 13 }} value={h.primary_cta_text || ''} onChange={e => update('hero.primary_cta_text', e.target.value)} /></Fld>
          <Fld label="Secondary CTA"><input className="form-input" style={{ fontSize: 13 }} value={h.secondary_cta_text || ''} onChange={e => update('hero.secondary_cta_text', e.target.value)} /></Fld>
        </div>
      </SectionCard>

      <SectionCard num="02" title="Shipping destinations (Globe)" enabled={true}>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12 }}>Destinations shown on the animated globe:</div>
        {dests.map((d, i) => (
          <div key={`dest-${i}`} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
            <input className="form-input" style={{ width: 120, fontSize: 13 }} value={d.name || ''} onChange={e => { const ds = [...dests]; ds[i] = { ...ds[i], name: e.target.value }; update('destinations', ds); }} />
            <input className="form-input" style={{ width: 80, fontSize: 13 }} type="number" step="0.1" value={d.lat || 0} onChange={e => { const ds = [...dests]; ds[i] = { ...ds[i], lat: parseFloat(e.target.value) || 0 }; update('destinations', ds); }} placeholder="Lat" />
            <input className="form-input" style={{ width: 80, fontSize: 13 }} type="number" step="0.1" value={d.lng || 0} onChange={e => { const ds = [...dests]; ds[i] = { ...ds[i], lng: parseFloat(e.target.value) || 0 }; update('destinations', ds); }} placeholder="Lng" />
            <button onClick={() => update('destinations', dests.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14 }}>✕</button>
          </div>
        ))}
        <button onClick={() => update('destinations', [...dests, { name: '', lat: 0, lng: 0 }])} className="btn btn-secondary btn-sm" style={{ fontSize: 10, marginTop: 4 }}>+ Add destination</button>
      </SectionCard>

      <SectionCard num="03" title="Yard location" enabled={y.enabled} onToggle={() => update('yard.enabled', !y.enabled)}>
        <Fld label="Eyebrow"><input className="form-input" style={{ fontSize: 13 }} value={y.eyebrow || ''} onChange={e => update('yard.eyebrow', e.target.value)} /></Fld>
        <Fld label="Heading"><input className="form-input" style={{ fontSize: 13 }} value={y.heading || ''} onChange={e => update('yard.heading', e.target.value)} /></Fld>
        <Fld label="Description"><textarea className="form-input" style={{ fontSize: 13, resize: 'vertical' }} rows={2} value={y.description || ''} onChange={e => update('yard.description', e.target.value)} /></Fld>
        <div className="form-row">
          <Fld label="Address"><input className="form-input" style={{ fontSize: 13 }} value={y.address || ''} onChange={e => update('yard.address', e.target.value)} /></Fld>
          <Fld label="Hours"><input className="form-input" style={{ fontSize: 13 }} value={y.hours || ''} onChange={e => update('yard.hours', e.target.value)} /></Fld>
        </div>
        <div className="form-row">
          <Fld label="Phone 1"><input className="form-input" style={{ fontSize: 13 }} value={y.phone1 || ''} onChange={e => update('yard.phone1', e.target.value)} /></Fld>
          <Fld label="Phone 2"><input className="form-input" style={{ fontSize: 13 }} value={y.phone2 || ''} onChange={e => update('yard.phone2', e.target.value)} /></Fld>
        </div>
      </SectionCard>
    </>
  );
}

function ContactEditor({ data, update }) {
  const h = data.hero || {};
  const wa = data.whatsapp || {};
  const fm = data.form || {};

  // Company contact info (separate from page content)
  const cfg = DB.get('appConfig') || {};
  const base = typeof cfg === 'object' && !Array.isArray(cfg) ? cfg : {};
  const ci = base.company_info || {};
  const [contact, setContact] = React.useState({
    whatsapp: ci.whatsapp_number || ci.mobile1 || '',
    phone: ci.telephone || '',
    email: ci.email || '',
    address: ci.address || '',
  });
  const [contactSaved, setContactSaved] = React.useState(false);

  const saveContact = () => {
    const cfgNow = DB.get('appConfig') || {};
    const baseNow = typeof cfgNow === 'object' && !Array.isArray(cfgNow) ? cfgNow : {};
    const updated = {
      ...baseNow,
      company_info: {
        ...(baseNow.company_info || {}),
        whatsapp_number: contact.whatsapp,
        mobile1: contact.whatsapp,
        telephone: contact.phone,
        email: contact.email,
        address: contact.address,
      }
    };
    DB.setAsync('appConfig', updated);
    setContactSaved(true);
    setTimeout(() => setContactSaved(false), 2500);
  };

  return (
    <>
      <SectionCard num="00" title="Contact Numbers" enabled={true}>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12 }}>These numbers appear on the website, WhatsApp buttons, and footer.</div>
        <Fld label="WhatsApp Number">
          <input className="form-input" style={{ fontSize: 13 }} value={contact.whatsapp} onChange={e => setContact(c => ({ ...c, whatsapp: e.target.value }))} placeholder="+971508583625" />
          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>Include country code, e.g. +971508583625</div>
        </Fld>
        <Fld label="Phone Number"><input className="form-input" style={{ fontSize: 13 }} value={contact.phone} onChange={e => setContact(c => ({ ...c, phone: e.target.value }))} placeholder="06-5433296" /></Fld>
        <Fld label="Email"><input className="form-input" style={{ fontSize: 13 }} value={contact.email} onChange={e => setContact(c => ({ ...c, email: e.target.value }))} placeholder="info@bigstaruae.com" /></Fld>
        <Fld label="Address"><input className="form-input" style={{ fontSize: 13 }} value={contact.address} onChange={e => setContact(c => ({ ...c, address: e.target.value }))} /></Fld>
        <button className="btn btn-primary btn-sm" style={{ marginTop: 8 }} onClick={saveContact}>
          {contactSaved ? '✓ Saved' : 'Save Contact Info'}
        </button>
      </SectionCard>
      <SectionCard num="01" title="Hero" enabled={true}>
        <Fld label="Eyebrow"><input className="form-input" style={{ fontSize: 13 }} value={h.eyebrow || ''} onChange={e => update('hero.eyebrow', e.target.value)} /></Fld>
        <Fld label="Heading"><input className="form-input" style={{ fontSize: 13 }} value={h.heading || ''} onChange={e => update('hero.heading', e.target.value)} /></Fld>
      </SectionCard>
      <SectionCard num="02" title="Contact form" enabled={fm.enabled !== false} onToggle={() => update('form.enabled', !(fm.enabled !== false))}>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>Form subject dropdown options:</div>
        {(fm.subjects || []).map((s, i) => (
          <div key={`subj-${i}`} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
            <input className="form-input" style={{ fontSize: 13 }} value={s} onChange={e => { const subs = [...fm.subjects]; subs[i] = e.target.value; update('form.subjects', subs); }} />
            <button onClick={() => update('form.subjects', fm.subjects.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14 }}>✕</button>
          </div>
        ))}
        <button onClick={() => update('form.subjects', [...(fm.subjects || []), ''])} className="btn btn-secondary btn-sm" style={{ fontSize: 10, marginTop: 4 }}>+ Add subject</button>
      </SectionCard>
      <SectionCard num="03" title="WhatsApp card" enabled={true}>
        <Fld label="Heading"><input className="form-input" style={{ fontSize: 13 }} value={wa.heading || ''} onChange={e => update('whatsapp.heading', e.target.value)} /></Fld>
        <Fld label="Description"><textarea className="form-input" style={{ fontSize: 13, resize: 'vertical' }} rows={2} value={wa.description || ''} onChange={e => update('whatsapp.description', e.target.value)} /></Fld>
      </SectionCard>
    </>
  );
}

function PartsEditor({ data, update }) {
  const h = data.hero || {};
  const cats = data.categories || [];

  return (
    <>
      <SectionCard num="01" title="Hero" enabled={h.enabled} onToggle={() => update('hero.enabled', !h.enabled)}>
        <Fld label="Eyebrow"><input className="form-input" style={{ fontSize: 13 }} value={h.eyebrow || ''} onChange={e => update('hero.eyebrow', e.target.value)} /></Fld>
        <Fld label="Headline"><input className="form-input" style={{ fontSize: 13 }} value={h.headline || ''} onChange={e => update('hero.headline', e.target.value)} /></Fld>
        <Fld label="Accent word"><input className="form-input" style={{ fontSize: 13 }} value={h.headline_accent_word || ''} onChange={e => update('hero.headline_accent_word', e.target.value)} /></Fld>
        <Fld label="Subheading"><textarea className="form-input" style={{ fontSize: 13, resize: 'vertical' }} rows={2} value={h.subheading || ''} onChange={e => update('hero.subheading', e.target.value)} /></Fld>
        <Fld label="CTA"><input className="form-input" style={{ fontSize: 13 }} value={h.cta_text || ''} onChange={e => update('hero.cta_text', e.target.value)} /></Fld>
      </SectionCard>
      <SectionCard num="02" title="Categories" enabled={true}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {cats.map((c, i) => (
            <label key={`cat-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', padding: '4px 0', minWidth: 140 }}>
              <input type="checkbox" checked={c.enabled !== false} onChange={e => { const cs = [...cats]; cs[i] = { ...cs[i], enabled: e.target.checked }; update('categories', cs); }} style={{ accentColor: 'var(--green)' }} />
              {c.name}
            </label>
          ))}
        </div>
      </SectionCard>
    </>
  );
}

function FooterEditor({ data, update }) {
  const ft = data;

  return (
    <>
      <SectionCard num="01" title="Footer content" enabled={true}>
        <Fld label="Description"><textarea className="form-input" style={{ fontSize: 13, resize: 'vertical' }} rows={2} value={ft.description || ''} onChange={e => update('description', e.target.value)} /></Fld>
        <Fld label="Copyright"><input className="form-input" style={{ fontSize: 13 }} value={ft.copyright_text || ''} onChange={e => update('copyright_text', e.target.value)} /></Fld>
        <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={ft.show_whatsapp_button !== false} onChange={e => update('show_whatsapp_button', e.target.checked)} style={{ accentColor: 'var(--green)' }} /> WhatsApp button
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={ft.show_phone_button !== false} onChange={e => update('show_phone_button', e.target.checked)} style={{ accentColor: 'var(--green)' }} /> Phone button
          </label>
        </div>
      </SectionCard>

      {(ft.columns || []).map((col, ci) => (
        <SectionCard key={`col-${ci}`} num={String(ci + 2).padStart(2, '0')} title={`Column: ${col.title}`} enabled={col.enabled} onToggle={() => { const cs = [...ft.columns]; cs[ci] = { ...cs[ci], enabled: !cs[ci].enabled }; update('columns', cs); }}>
          <Fld label="Column title"><input className="form-input" style={{ fontSize: 13 }} value={col.title || ''} onChange={e => { const cs = [...ft.columns]; cs[ci] = { ...cs[ci], title: e.target.value }; update('columns', cs); }} /></Fld>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Links:</div>
          {(col.links || []).map((link, li) => (
            <div key={`link-${ci}-${li}`} style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'center' }}>
              <input type="checkbox" checked={link.enabled !== false} onChange={e => { const cs = [...ft.columns]; const ls = [...cs[ci].links]; ls[li] = { ...ls[li], enabled: e.target.checked }; cs[ci] = { ...cs[ci], links: ls }; update('columns', cs); }} style={{ accentColor: 'var(--green)' }} />
              <input className="form-input" style={{ fontSize: 13 }} value={link.label || ''} onChange={e => { const cs = [...ft.columns]; const ls = [...cs[ci].links]; ls[li] = { ...ls[li], label: e.target.value }; cs[ci] = { ...cs[ci], links: ls }; update('columns', cs); }} />
              <button onClick={() => { const cs = [...ft.columns]; cs[ci] = { ...cs[ci], links: cs[ci].links.filter((_, j) => j !== li) }; update('columns', cs); }} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 12 }}>✕</button>
            </div>
          ))}
          <button onClick={() => { const cs = [...ft.columns]; cs[ci] = { ...cs[ci], links: [...(cs[ci].links || []), { label: 'New link', enabled: true }] }; update('columns', cs); }} className="btn btn-secondary btn-sm" style={{ fontSize: 10, marginTop: 4 }}>+ Add link</button>
        </SectionCard>
      ))}

      <SectionCard num={String((ft.columns || []).length + 2).padStart(2, '0')} title="Bottom links" enabled={true}>
        {(ft.bottom_links || []).map((link, i) => (
          <div key={`bl-${i}`} style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={link.enabled !== false} onChange={e => { const ls = [...ft.bottom_links]; ls[i] = { ...ls[i], enabled: e.target.checked }; update('bottom_links', ls); }} style={{ accentColor: 'var(--green)' }} />
            <input className="form-input" style={{ fontSize: 13 }} value={link.label || ''} onChange={e => { const ls = [...ft.bottom_links]; ls[i] = { ...ls[i], label: e.target.value }; update('bottom_links', ls); }} />
            <button onClick={() => update('bottom_links', ft.bottom_links.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 12 }}>✕</button>
          </div>
        ))}
        <button onClick={() => update('bottom_links', [...(ft.bottom_links || []), { label: 'New link', enabled: true }])} className="btn btn-secondary btn-sm" style={{ fontSize: 10, marginTop: 4 }}>+ Add link</button>
      </SectionCard>
    </>
  );
}

/* ── Used Cars editor ── */
function InventoryEditor({ data, update }) {
  const h = data.hero || {};
  return (
    <>
      <SectionCard num="01" title="Hero" enabled={true}>
        <Fld label="Eyebrow"><input className="form-input" style={{ fontSize: 13 }} value={h.eyebrow || ''} onChange={e => update('hero.eyebrow', e.target.value)} /></Fld>
        <Fld label="Heading"><input className="form-input" style={{ fontSize: 13 }} value={h.heading || ''} onChange={e => update('hero.heading', e.target.value)} /></Fld>
        <Fld label="Subheading"><textarea className="form-input" style={{ fontSize: 13, resize: 'vertical' }} rows={2} value={h.subheading || ''} onChange={e => update('hero.subheading', e.target.value)} /></Fld>
        <Fld label="Default view">
          <div style={{ display: 'flex', gap: 6 }}>
            {['grid', 'list'].map(v => (
              <button key={v} onClick={() => update('default_view', v)} className={`btn btn-sm ${(data.default_view || 'grid') === v ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: 11, textTransform: 'capitalize' }}>{v}</button>
            ))}
          </div>
        </Fld>
      </SectionCard>
      <SectionCard num="02" title="Unit detail — pricing note" enabled={true}>
        <Fld label="Title"><input className="form-input" style={{ fontSize: 13 }} value={data.pricing_note_title || ''} onChange={e => update('pricing_note_title', e.target.value)} placeholder="Pricing on inquiry" /></Fld>
        <Fld label="Description"><textarea className="form-input" style={{ fontSize: 13, resize: 'vertical' }} rows={2} value={data.pricing_note_text || ''} onChange={e => update('pricing_note_text', e.target.value)} placeholder="Tailored quote with VAT, export & shipping in one message." /></Fld>
      </SectionCard>
    </>
  );
}

/* ── Trade page editor ── */
function TradeEditor({ data, update }) {
  const h = data.hero || {};
  const docs = data.required_documents || [];
  return (
    <>
      <SectionCard num="01" title="Hero" enabled={true}>
        <Fld label="Eyebrow"><input className="form-input" style={{ fontSize: 13 }} value={h.eyebrow || ''} onChange={e => update('hero.eyebrow', e.target.value)} /></Fld>
        <Fld label="Heading"><input className="form-input" style={{ fontSize: 13 }} value={h.heading || ''} onChange={e => update('hero.heading', e.target.value)} /></Fld>
        <Fld label="Subheading"><textarea className="form-input" style={{ fontSize: 13, resize: 'vertical' }} rows={2} value={h.subheading || ''} onChange={e => update('hero.subheading', e.target.value)} /></Fld>
      </SectionCard>
      <SectionCard num="02" title="Required documents" enabled={true}>
        {docs.map((d, i) => (
          <div key={`tdoc-${i}`} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
            <input className="form-input" style={{ fontSize: 13, flex: 1 }} value={d.name || ''} onChange={e => { const ds = [...docs]; ds[i] = { ...ds[i], name: e.target.value }; update('required_documents', ds); }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, whiteSpace: 'nowrap', cursor: 'pointer' }}>
              <input type="checkbox" checked={d.required !== false} onChange={e => { const ds = [...docs]; ds[i] = { ...ds[i], required: e.target.checked }; update('required_documents', ds); }} style={{ accentColor: 'var(--red)' }} /> Required
            </label>
            <button onClick={() => update('required_documents', docs.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14 }}>✕</button>
          </div>
        ))}
        <button onClick={() => update('required_documents', [...docs, { name: '', required: false, hint: '' }])} className="btn btn-secondary btn-sm" style={{ fontSize: 10, marginTop: 4 }}>+ Add document type</button>
      </SectionCard>
    </>
  );
}

/* ══════════════════════════════════════════════════════ */
/*  MAIN SITE CONTENT — thin wrapper, editors external   */
/* ══════════════════════════════════════════════════════ */
function CMSSiteContent() {
  const [activePage, setActivePage] = useState('website_homepage');
  const [data, setData] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState('');
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);

  // FIX 1: Load from Supabase via DB.refresh, then read localStorage
  useEffect(() => {
    async function load() {
      try { await DB.refresh('appConfig'); } catch {}
      const cfg = DB.get('appConfig') || {};
      const base = typeof cfg === 'object' && !Array.isArray(cfg) ? cfg : {};
      const loaded = {};
      PAGES.forEach(p => {
        loaded[p.id] = base[p.id] ? { ...DEFAULTS[p.id], ...base[p.id] } : { ...DEFAULTS[p.id] };
      });
      setData(loaded);
      setLoading(false);
    }
    load();
  }, []);

  // FIX 3: Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = 'You have unsaved changes.'; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const pageData = data[activePage] || DEFAULTS[activePage] || {};

  // FIX 2: Optimized update — no deep clone
  const update = (path, value) => {
    setData(prev => {
      const next = { ...prev };
      const pd = { ...(prev[activePage] || {}) };
      next[activePage] = pd;
      const parts = path.split('.');
      if (parts.length === 1) {
        pd[parts[0]] = value;
      } else if (parts.length === 2) {
        pd[parts[0]] = { ...(pd[parts[0]] || {}), [parts[1]]: value };
      } else if (parts.length === 3) {
        const sec = { ...(pd[parts[0]] || {}) };
        sec[parts[1]] = { ...(sec[parts[1]] || {}), [parts[2]]: value };
        pd[parts[0]] = sec;
      }
      return next;
    });
    setDirty(true);
  };

  // FIX 1: Publish uses DB.setAsync to await Supabase push
  const publish = async () => {
    setPublishing(true);
    try {
      const cfg = DB.get('appConfig') || {};
      const base = typeof cfg === 'object' && !Array.isArray(cfg) ? cfg : {};
      const updated = { ...base };
      Object.keys(data).forEach(k => { updated[k] = data[k]; });
      if (data.website_homepage?.hero) {
        updated.website_hero = {
          type: data.website_homepage.hero.background_mode || 'animation',
          image_url: data.website_homepage.hero.background_image_url || '',
          video_url: data.website_homepage.hero.background_video_url || '',
          overlay_opacity: data.website_homepage.hero.overlay_opacity ?? 0.7,
        };
      }
      await DB.setAsync('appConfig', updated);
      setDirty(false);
      setSaved('✓ Published');
      setTimeout(() => setSaved(''), 3000);
    } catch (err) {
      console.error('Publish failed:', err);
      alert('Failed to publish. Please try again.');
    }
    setPublishing(false);
  };

  const renderEditor = () => {
    switch (activePage) {
      case 'website_homepage': return <HomepageEditor data={pageData} update={update} />;
      case 'website_about': return <AboutEditor data={pageData} update={update} />;
      case 'website_contact': return <ContactEditor data={pageData} update={update} />;
      case 'website_parts': return <PartsEditor data={pageData} update={update} />;
      case 'website_inventory': return <InventoryEditor data={pageData} update={update} />;
      case 'website_trade': return <TradeEditor data={pageData} update={update} />;
      case 'website_footer': return <FooterEditor data={pageData} update={update} />;
      default: return <HomepageEditor data={pageData} update={update} />;
    }
  };

  const handlePreview = async () => {
    if (dirty) {
      if (window.confirm('Publish changes first so preview shows the latest content?')) {
        await publish();
      }
    }
    window.open('/?preview=true', '_blank');
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 20px', color: 'var(--text3)' }}>
        <div style={{ fontSize: 24, marginBottom: 12 }}>⏳</div>
        <div style={{ fontSize: 14 }}>Loading content from server...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Website</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text1)', marginBottom: 4 }}>Site content</h2>
          <p style={{ fontSize: 12, color: 'var(--text3)' }}>Edit every page on bigstar.com — copy, sections, and media.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={handlePreview} className="btn btn-secondary btn-sm">
            👁 Preview{dirty ? ' (publish first)' : ''}
          </button>
          <button onClick={publish} disabled={publishing} className="btn btn-primary btn-sm" style={{ background: dirty ? '#22C55E' : undefined, position: 'relative' }}>
            {dirty && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', position: 'absolute', top: -2, right: -2, animation: 'pulse 1.5s infinite' }} />}
            {publishing ? 'Publishing...' : dirty ? '● Publish changes' : '✓ Published'}
          </button>
          {saved && <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600 }}>{saved}</span>}
        </div>
      </div>

      {/* Layout: sidebar + editor */}
      <div style={{ display: 'flex', gap: 20 }}>
        {/* Page sidebar */}
        <div style={{ width: 180, flexShrink: 0 }}>
          {PAGES.map(p => (
            <button key={p.id} onClick={() => setActivePage(p.id)} style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 12px',
              borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, textAlign: 'left',
              background: activePage === p.id ? 'var(--blue)' : 'transparent',
              color: activePage === p.id ? '#fff' : 'var(--text2)',
              fontWeight: activePage === p.id ? 600 : 400,
              marginBottom: 2, transition: 'all 0.15s',
            }}
              onMouseEnter={e => { if (activePage !== p.id) e.target.style.background = 'var(--bg2)'; }}
              onMouseLeave={e => { if (activePage !== p.id) e.target.style.background = 'transparent'; }}>
              <span>{p.icon}</span> {p.label}
            </button>
          ))}
        </div>

        {/* Editor */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {renderEditor()}
        </div>
      </div>

      <style>{`@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </div>
  );
}

export default CMSSiteContent;
